import {useState} from "react";
import {App as AntdApp, Table, Tag, Modal, Input} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon, Loading, PageHeader} from "~/components";
import {useDebounce} from "~/hooks";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetSeoReportsQuery,
	useGetSeoReportQuery,
	useDeleteSeoReportMutation,
} from "~/reduxs/api/seoApiSlice";
import SeoResult from "../components/SeoResult";
import style from "../style/SeoCheck.module.scss";

const cn = className.bind(style);

// Màu tag theo xếp hạng.
const GRADE_COLOR = {A: 'green', B: 'lime', C: 'gold', D: 'orange', F: 'red'};

function SeoHistory() {

	const {notification, modal} = AntdApp.useApp();

	const [keyword, setKeyword] = useState('');
	const search = useDebounce(keyword.trim());
	const {data: reports = [], isLoading, isFetching} = useGetSeoReportsQuery(search);
	const [deleteSeoReport] = useDeleteSeoReportMutation();

	// Báo cáo đang xem chi tiết (tải riêng để có groups/items/meta đầy đủ).
	const [viewId, setViewId] = useState(null);
	const {data: detail, isFetching: loadingDetail} = useGetSeoReportQuery(viewId, {skip: !viewId});

	const handleDelete = (report) => {
		modal.confirm({
			title: 'Xóa báo cáo',
			content: `Xóa báo cáo chấm điểm cho "${report.domain}"?`,
			okText: 'Xóa',
			okButtonProps: {danger: true},
			cancelText: 'Hủy',
			onOk: async () => {
				try {
					await deleteSeoReport(report.id).unwrap();
					notification.success({message: 'Thành công', description: 'Đã xóa báo cáo'});
				} catch (e) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Xóa thất bại')});
				}
			},
		});
	};

	const columns = [
		{title: 'Domain', dataIndex: 'domain', key: 'domain'},
		{
			title: 'Điểm', dataIndex: 'score', key: 'score', width: 100,
			sorter: (a, b) => a.score - b.score,
			render: (score, r) => (
				<span><strong>{score}</strong> <Tag color={GRADE_COLOR[r.grade] || 'default'}>{r.grade}</Tag></span>
			),
		},
		{
			title: 'Trạng thái', key: 'status', width: 140,
			render: (_, r) => (
				<>
					<Tag color={r.ssl ? 'green' : 'red'}>{r.ssl ? 'HTTPS' : 'HTTP'}</Tag>
					<Tag>{r.status_code || '—'}</Tag>
				</>
			),
		},
		{title: 'Thời gian', dataIndex: 'created', key: 'created', width: 170},
		{
			title: '', key: 'actions', width: 120,
			render: (_, r) => (
				<div className={cn('table-actions')}>
					<Button small blue onClick={() => setViewId(r.id)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-eye" />} />
					<Button small red onClick={() => handleDelete(r)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
				</div>
			),
		},
	];

	if (isLoading) return <Loading />;

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-clock-rotate-left"
				title="Lịch sử chấm điểm"
				subtitle="Các lần chấm điểm SEO đã thực hiện"
				actions={
					<Button to="/seo" primary
						leftIcon={<FontAwesomeIcon icon="fa-light fa-magnifying-glass-chart" />}>
						Chấm điểm mới
					</Button>
				}
			/>

			<Input
				allowClear
				prefix={<FontAwesomeIcon icon="fa-light fa-magnifying-glass" />}
				placeholder="Tìm theo domain…"
				value={keyword}
				onChange={(e) => setKeyword(e.target.value)}
				style={{maxWidth: 320, marginBottom: 14}}
			/>

			<Table
				rowKey="id"
				columns={columns}
				dataSource={reports}
				loading={isFetching}
				locale={{emptyText: search ? `Không có báo cáo nào khớp “${search}”` : 'Chưa có báo cáo nào'}}
				pagination={{pageSize: 10, hideOnSinglePage: true}}
			/>

			<Modal
				open={!!viewId}
				onCancel={() => setViewId(null)}
				footer={null}
				width={900}
				title={detail ? `Báo cáo: ${detail.domain}` : 'Báo cáo SEO'}
			>
				{loadingDetail ? <Loading noFixed /> : <SeoResult result={detail} showPreview={false} />}
			</Modal>
		</div>
	);
}

export default SeoHistory;
